import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import type { RegistryAttention } from '@/types/registry-documents';
import { useLeaderPoll } from './useLeaderPoll';

/** A delivery or an overdue order is acted on within the hour, not the
 * minute. */
export const REGISTRY_ATTENTION_POLL_MS = 60_000;

/** Tab-channel key the leader shares the counts under (see `useLeaderPoll`). */
export const REGISTRY_ATTENTION_POLL_KEY = 'registry-attention';

/**
 * The Admin menu's registry-documents counts: open inbox items for
 * documents that arrived, for orders past their delivery window and for
 * replies from the research desk nobody has read. `null` until the first
 * value lands; errors keep the last known value rather than flashing the
 * badge away.
 *
 * Mounted in the shell of every admin tab, so it runs through the leader
 * poll: one tab fetches, the rest receive the value. The endpoint is
 * admin-only, hence `enabled`.
 */
export function useRegistryAttention(enabled: boolean): RegistryAttention | null {
  const { data } = useLeaderPoll<RegistryAttention>(REGISTRY_ATTENTION_POLL_KEY, {
    enabled,
    interval: REGISTRY_ATTENTION_POLL_MS,
    queryFn: ({ signal }) => vnextApiClient.getRegistryAttention({ signal }),
    onError: (error) => Sentry.captureException(error),
  });
  return data ?? null;
}
